import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateFilterFn } from '@angular/material/datepicker';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { ContentTypeEnum } from '../../../../models/enums/content-type.enum';
import { RolesEnum } from '../../../../models/enums/roles.enum';
import { FileShareModel } from '../../../../models/file-share.model';
import { RoleModel } from '../../../../models/role.model';
import { ErrorsService } from '../../../../services/errors.service';
import { FileSharesResource } from '../../../../services/resources/file-shares.resource';
import { RolesResource } from '../../../../services/resources/roles.resource';
import { UserSessionService } from '../../../../services/user-session.service';

@Component({
    selector: 'sl-file-sharing-form',
    templateUrl: './file-sharing-form.component.html',
    styleUrls: ['./file-sharing-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FileSharingFormComponent implements OnInit, OnDestroy {
    readonly allowedMimeTypes = [
        'application/xml',
        'text/xml',
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpeg',
        'application/pdf'
    ];

    roles: RoleModel[] = [];
    isLoading = false;
    formGroup: UntypedFormGroup;
    maxExpirationFilter: DateFilterFn<Date>;
    get canDisplayRoles(): boolean {
        return this.userSessionService.roleId >= RolesEnum.Operator;
    }

    private subscription = new Subscription();

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly dialogRef = inject(MatDialogRef);
    private readonly fileSharesResource = inject(FileSharesResource);
    private readonly rolesResource = inject(RolesResource);
    private readonly errorsService = inject(ErrorsService);
    private readonly userSessionService = inject(UserSessionService);

    constructor() {
        const today = new Date();
        const yesterday = new Date(today.setDate(today.getDate() - 1));
        const sixMonthsFromNow = new Date(today.setMonth(today.getMonth() + 6));
        this.maxExpirationFilter = (d: Date | null): boolean => {
            const selectedDate = d || new Date();
            return selectedDate >= yesterday && selectedDate <= sixMonthsFromNow;
        };
    }

    ngOnInit(): void {
        const errorSub = this.errorsService.onBusinessException.subscribe(() => (this.isLoading = false));
        this.formGroup = this.formBuilder.group({
            roleId: new FormControl(undefined, undefined),
            expiresByUtc: new FormControl(undefined, Validators.required),
            fileName: new FormControl(undefined, Validators.required),
            mimeType: new FormControl(undefined, Validators.required),
            data: new FormControl(undefined, Validators.required)
        });

        this.rolesResource.getAll().subscribe(r => this.setupRoles(r.value));
        this.subscription.add(errorSub);
    }

    ngOnDestroy(): void {
        if (!!this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    fileSelected(event: any): void {
        const reader = new FileReader();

        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];

            reader.readAsDataURL(file);
            reader.onload = () => {
                const fileType = file.type && file.type.length > 0 ? file.type : ContentTypeEnum.ApplicationOctetStream;

                this.formGroup.controls['fileName'].setValue(file.name);
                this.formGroup.controls['mimeType'].setValue(fileType);
                this.formGroup.controls['data'].setValue(reader.result?.toString()?.split(',')?.at(1));
            };
        }
    }

    upload(): void {
        if (!this.canDisplayRoles) {
            this.formGroup.controls['roleId'].setValue(this.userSessionService.roleId);
        }

        this.formGroup.updateValueAndValidity();

        if (this.formGroup.valid) {
            this.isLoading = true;
            const model = this.formGroup.getRawValue() as FileShareModel;

            // HACK: add timezone offset to expiry date, so it gets sent to backend correctly as UTC
            model.expiresByUtc = new Date(model.expiresByUtc.getTime() - model.expiresByUtc.getTimezoneOffset() * 60_000);

            this.fileSharesResource.add(model).subscribe(r => {
                this.isLoading = false;
                this.dialogRef.close(`${r.value.result}`);
            });
        }
    }

    private setupRoles(roles: RoleModel[]): void {
        this.roles = roles.filter(r => +r.id > RolesEnum.Anonymous);
    }
}
